import React, { useState } from "react";
import { AlertCircle, Flame, HeartPulse, Activity, Moon, Zap, ShieldCheck, Info } from "lucide-react";
import { calcComorbidityClusters } from "./helpers";
import WidgetInfoTooltip from "./WidgetInfoTooltip";

const FACTOR_ICONS = {
  whtr: Flame,
  bp: HeartPulse,
  imc: Activity,
  sleep: Moon,
  sedentary: Activity,
  stress: Zap,
};

const CLUSTER_FACTOR_DETAILS = {
  whtr: {
    title: "Grasa Visceral (WHtR > 0.50)",
    what: "Acumulación central de tejido adiposo intra-abdominal.",
    calculation: "Perímetro cintura / Talla > 0.50.",
    standard: "IDF & Ashwell 2012 Consensus.",
    relevance: "Predictor cardiometabólico más certero que el IMC para riesgo vascular.",
  },
  bp: {
    title: "Tensión Arterial Elevada",
    what: "Presión arterial en reposo por encima de rangos óptimos de salud cardiovascular.",
    calculation: "PAS ≥ 130 mmHg o PAD ≥ 85 mmHg.",
    standard: "ACC/AHA 2017 Guidelines.",
    relevance: "Principal detonante asintomático de emergencias vasculares y cardiopatía isquémica.",
  },
  imc: {
    title: "Sobrepeso / Obesidad (IMC ≥ 25)",
    what: "Exceso de masa corporal respecto a la estatura del colaborador.",
    calculation: "Peso (kg) / Talla² (m²) ≥ 25.0 kg/m².",
    standard: "Organización Mundial de la Salud (OMS 2000).",
    relevance: "Sobrecarga osteoarticular postural y acelerador de resistencia insulínica.",
  },
  sleep: {
    title: "Déficit de Sueño (< 7h)",
    what: "Duración insuficiente de descanso nocturno biológicamente reparador.",
    calculation: "Promedio de sueño reportado < 7 horas por noche.",
    standard: "National Sleep Foundation (NSF) & AASM Consensus.",
    relevance: "Fatiga diurna, menor tiempo de reacción y riesgo de accidentes laborales.",
  },
  sedentary: {
    title: "Sedentarismo Ocupacional",
    what: "Bajo gasto energético y permanencia sentada continuada durante la jornada.",
    calculation: "< 600 MET-min/sem en IPAQ o > 8h al día en postura sedente.",
    standard: "OMS Directrices Mundiales sobre Actividad Física y Sedentarismo 2020.",
    relevance: "Atrofia postural, lumbalgias incapacitantes y enlentecimiento metabólico.",
  },
  stress: {
    title: "Tensión Autonómica Elevada",
    what: "Sobrecarga sostenida del sistema neurovegetativo simpático.",
    calculation: "Índice de Tensión de Baevsky > 400 pts (rPPG FaceScan).",
    standard: "Baevsky RM Stress Index & Task Force ESC/NASPE 1996.",
    relevance: "Acelerador de agotamiento biológico, presentismo y síndrome de burnout.",
  },
};

export default function ComorbidityClusterCard({ employees = [], forceSimulated = false }) {
  const data = calcComorbidityClusters(employees, forceSimulated);
  const [selectedCluster, setSelectedCluster] = useState(null);

  const { optimal, moderate, high } = data.clusters;

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm relative"
      data-testid="comorbidity-cluster-card"
    >
      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
              <AlertCircle size={12} />
              Carga Multirriesgo & Síndrome Metabólico
            </span>
            <span className="text-xs text-slate-400">
              Muestra evaluada: <strong>{data.total}</strong> colaboradores
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <h3 className="text-base font-bold text-slate-800">
              Matriz de Acumulación de Factores de Riesgo
            </h3>
            <WidgetInfoTooltip
              title="Matriz de Acumulación de Riesgo y Síndrome Metabólico"
              align="left"
              what="Identificación de la carga patológica concurrente en cada colaborador a partir de los 6 factores de riesgo modificables principales."
              calculation="Suma de factores presentes: Grasa visceral (WHtR > 0.50), Tensión elevada (≥130/85), Sobrepeso/Obesidad (IMC ≥ 25), Déficit de sueño (<7h), Sedentarismo y Tensión autonómica (>400 pts). Clasificados en: Resilientes (0), Riesgo Moderado (1-2) y Clúster Metabólico (3+)."
              standard="NCEP ATP III / AHA / NHLBI / IDF 2009 Unified Metabolic Syndrome Consensus & WHO Chronic Disease Framework."
              relevance="La co-existencia de 3 o más factores multiplica por 5 el riesgo de ausentismo médico prolongado, accidentes laborales graves e incapacidades permanentes."
            />
          </div>
          <p className="text-xs text-slate-500">
            Identificación de co-ocurrencia simultánea de 6 factores de riesgo modificables.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/60">
          <Info size={14} className="text-slate-400" />
          <span>Predicción de ausentismo médico ocupacional</span>
        </div>
      </div>

      {/* Barra de Distribución Apilada de Clústeres */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-2">
          <span>Distribución de la Plantilla por Carga de Factores</span>
          <span className="text-slate-400 font-normal">Base: 100% de evaluados</span>
        </div>
        <div className="w-full h-4 rounded-full overflow-hidden flex bg-slate-100 p-0.5 border border-slate-200">
          <div
            style={{ width: `${optimal.percent}%`, backgroundColor: optimal.color }}
            className="h-full rounded-l-full transition-all duration-700 cursor-pointer hover:opacity-90"
            title={`${optimal.label}: ${optimal.percent}% (${optimal.count})`}
            onClick={() => setSelectedCluster(selectedCluster === "optimal" ? null : "optimal")}
          />
          <div
            style={{ width: `${moderate.percent}%`, backgroundColor: moderate.color }}
            className="h-full transition-all duration-700 cursor-pointer hover:opacity-90"
            title={`${moderate.label}: ${moderate.percent}% (${moderate.count})`}
            onClick={() => setSelectedCluster(selectedCluster === "moderate" ? null : "moderate")}
          />
          <div
            style={{ width: `${high.percent}%`, backgroundColor: high.color }}
            className="h-full rounded-r-full transition-all duration-700 cursor-pointer hover:opacity-90"
            title={`${high.label}: ${high.percent}% (${high.count})`}
            onClick={() => setSelectedCluster(selectedCluster === "high" ? null : "high")}
          />
        </div>
      </div>

      {/* 3 Tarjetas Bento de Clúster */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 mb-6">
        {/* Clúster 1: 0 Factores */}
        <div
          onClick={() => setSelectedCluster(selectedCluster === "optimal" ? null : "optimal")}
          className={`p-4 rounded-xl border transition-all cursor-pointer ${
            selectedCluster === "optimal"
              ? "ring-2 ring-emerald-500 border-emerald-300 bg-emerald-50/70 shadow-sm"
              : "border-slate-200/80 bg-white hover:bg-slate-50/60"
          }`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-emerald-700 bg-emerald-100/60 px-2 py-0.5 rounded-full">
                {optimal.percent}% ({optimal.count})
              </span>
              <WidgetInfoTooltip
                title="Clúster 0 Factores: Perfil Resiliente"
                size="sm"
                align="center"
                what="Colaboradores sin acumulación de factores de riesgo cardiometabólicos o psicosociales."
                calculation="0 de 6 factores presentes (WHtR ≤ 0.50, PA < 130/85, IMC < 25, Sueño ≥ 7h, Activo, Estrés ≤ 400)."
                standard="AHA Life's Essential 8 & OMS Resiliencia Metabólica."
                relevance="Blindaje de la fuerza de trabajo saludable y prevención del desgaste temprano."
              />
            </div>
          </div>
          <h4 className="text-xs font-bold text-slate-800">{optimal.label}</h4>
          <p className="text-[11px] text-slate-500 mb-2">{optimal.sublabel}</p>
          <p className="text-[10px] text-emerald-700 font-medium bg-emerald-50 p-2 rounded-lg border border-emerald-100">
            {optimal.action}
          </p>
        </div>

        {/* Clúster 2: 1-2 Factores */}
        <div
          onClick={() => setSelectedCluster(selectedCluster === "moderate" ? null : "moderate")}
          className={`p-4 rounded-xl border transition-all cursor-pointer ${
            selectedCluster === "moderate"
              ? "ring-2 ring-amber-500 border-amber-300 bg-amber-50/70 shadow-sm"
              : "border-slate-200/80 bg-white hover:bg-slate-50/60"
          }`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-amber-700 bg-amber-100/60 px-2 py-0.5 rounded-full">
                {moderate.percent}% ({moderate.count})
              </span>
              <WidgetInfoTooltip
                title="Clúster 1-2 Factores: Riesgo Moderado"
                size="sm"
                align="center"
                what="Presencia de 1 o 2 factores aislados en fase subclínica o reversible."
                calculation="Cumplimiento de 1 o 2 de los 6 factores de corte clínico evaluados."
                standard="OIT / OMS Directrices de Prevención Primaria en el Trabajo."
                relevance="Ventana idónea para intervención de bajo costo antes de consolidar el síndrome metabólico."
              />
            </div>
          </div>
          <h4 className="text-xs font-bold text-slate-800">{moderate.label}</h4>
          <p className="text-[11px] text-slate-500 mb-2">{moderate.sublabel}</p>
          <p className="text-[10px] text-amber-800 font-medium bg-amber-50 p-2 rounded-lg border border-amber-100">
            {moderate.action}
          </p>
        </div>

        {/* Clúster 3: 3+ Factores (Alarma) */}
        <div
          onClick={() => setSelectedCluster(selectedCluster === "high" ? null : "high")}
          className={`p-4 rounded-xl border transition-all cursor-pointer ${
            selectedCluster === "high"
              ? "ring-2 ring-rose-500 border-rose-300 bg-rose-50/70 shadow-sm"
              : "border-slate-200/80 bg-white hover:bg-slate-50/60"
          }`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" />
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-rose-700 bg-rose-100/60 px-2 py-0.5 rounded-full">
                {high.percent}% ({high.count})
              </span>
              <WidgetInfoTooltip
                title="Clúster 3+ Factores: Alarma Metabólica"
                size="sm"
                align="right"
                what="Concurrencia patológica de 3 o más factores de riesgo simultáneos."
                calculation="Cumplimiento de ≥ 3 de los 6 factores de corte clínico."
                standard="Consenso NCEP ATP III / IDF / AHA 2009 (Criterios Síndrome Metabólico)."
                relevance="Multiplica por 5 el ausentismo laboral prolongado y la probabilidad de incapacidad severa."
              />
            </div>
          </div>
          <h4 className="text-xs font-bold text-slate-800">{high.label}</h4>
          <p className="text-[11px] text-slate-500 mb-2">{high.sublabel}</p>
          <p className="text-[10px] text-rose-800 font-medium bg-rose-50 p-2 rounded-lg border border-rose-100">
            {high.action}
          </p>
        </div>
      </div>

      {/* Desglose de Prevalencia de los 6 Factores Ocupacionales */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
          Prevalencia Individual de Factores de Riesgo en la Plantilla
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.factorPrevalence.map((fact) => {
            const Icon = FACTOR_ICONS[fact.id] || Activity;
            const details = CLUSTER_FACTOR_DETAILS[fact.id];
            return (
              <div
                key={fact.id}
                className="p-3 rounded-xl bg-slate-50/80 border border-slate-200/60 flex flex-col justify-between"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-700">
                      <Icon size={13} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 leading-tight">
                      {fact.label}
                    </span>
                    {details && (
                      <WidgetInfoTooltip
                        title={details.title}
                        size="sm"
                        align="left"
                        what={details.what}
                        calculation={details.calculation}
                        standard={details.standard}
                        relevance={details.relevance}
                      />
                    )}
                  </div>
                  <span className="text-xs font-extrabold text-slate-900">
                    {fact.percent}%
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max(3, fact.percent)}%`,
                      backgroundColor: fact.color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
